import {
  CatalogsQuery,
  IngredientsToAvoidQuery,
} from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import { TagCategory } from 'src/services/panacea/types';
import { NUTRITION_CATEGORY_CONFIG } from 'src/ts/types';
import { ClientSafeTenantConfig } from 'server/config-cache/types';

// @TODO: Really macro keys, their display names, and units should be pulled
// from an API endpoint.
export type FilterMacro = {
  key: string;
  displayName: string;
  min: number;
  max: number | null;
  unit: string;
};

export type DisplayFilters = {
  tagCategories?: TagCategory[];
  ingredientsToAvoid?: IngredientsToAvoidQuery['ingredientsToAvoid'];
  macros?: FilterMacro[];
  chefNames?: string[];
};

export function getDisplayFilters(input: {
  catalogs: CatalogsQuery['catalogs'];
  ingredientsToAvoid: IngredientsToAvoidQuery['ingredientsToAvoid'];
  tagCategories: TagCategory[];
  tenantConfig: ClientSafeTenantConfig;
  // The tenant tags can be further filtered down to specific pages
  tenantPageFilter?: 'customizer' | 'funnel';
}): DisplayFilters {
  const {
    catalogs,
    ingredientsToAvoid,
    tagCategories,
    tenantConfig,
    tenantPageFilter,
  } = input;

  return {
    tagCategories: getTagCategories({
      catalogs,
      tagCategories,
      tenantConfig,
      tenantPageFilter,
    }),
    ingredientsToAvoid: getIngredientsToAvoid({ ingredientsToAvoid }),
    macros: getMacros({ catalogs }),
    chefNames: getChefs({ catalogs, tenantConfig }),
  };
}

/**
 * Tag Categories are filtered down to tags who show up on the menu and what dietary templates
 * the tenant has configured to be visible.
 */
function getTagCategories(input: {
  catalogs: CatalogsQuery['catalogs'];
  tagCategories: TagCategory[];
  tenantConfig: ClientSafeTenantConfig;
  tenantPageFilter?: 'customizer' | 'funnel';
}): TagCategory[] {
  const { catalogs, tagCategories, tenantConfig, tenantPageFilter } = input;

  const uniqueCatalogTagIds = new Set<number>();
  catalogs.forEach((catalog) => {
    catalog.items.forEach((item) => {
      item?.variant.tags
        .filter((tag) => !!tag)
        .forEach((tag) => {
          uniqueCatalogTagIds.add(tag.id);
        });
    });
  });

  // Filter down tagCategories and their tags. Remove any tag categories who have zero tags
  const filteredTagCategories = tagCategories
    .map((tagCategory) => ({
      ...tagCategory,
      tags: tagCategory.tags
        // Filter out any existing dietary templates and top-rated tags, as these are driven from tenant config.
        .filter(() => {
          return ['DIETARY_TEMPLATE', 'TOP_RATED_BY_USER'].every(
            (ignoredLogicalId) => ignoredLogicalId !== tagCategory.logicalId,
          );
        })
        .filter((tag) => uniqueCatalogTagIds.has(tag.id))
        .sort((a, b) => {
          if (a.sortOrder > b.sortOrder) return 1;
          return -1;
        }),
    }))
    .filter((tagCategory) => {
      const containsTags = tagCategory.tags.length > 0;
      const disabledForTenant =
        tenantConfig.hidePrimaryProteinFilter &&
        tagCategory.logicalId === 'PRIMARY_PROTEIN';

      return containsTags && !disabledForTenant;
    });

  // Append the tenant-driven tag categories
  filteredTagCategories.push(
    ...buildTenantConfigDrivenTagCategories({
      tenantConfig,
      tagCategories,
      tenantPageFilter,
      uniqueCatalogTagIds: Array.from(uniqueCatalogTagIds),
    }),
  );

  return filteredTagCategories.sort((a, b) => a.sortOrder - b.sortOrder);
}

export function buildTenantConfigDrivenTagCategories(input: {
  tenantConfig: ClientSafeTenantConfig;
  tagCategories: TagCategory[];
  tenantPageFilter?: 'customizer' | 'funnel';
  // When provided, this is used to determine if the top-rated tag category should be returned and displayed.
  uniqueCatalogTagIds?: number[];
}): TagCategory[] {
  const { tenantConfig, tagCategories, tenantPageFilter, uniqueCatalogTagIds } =
    input;

  // The list of dietary template tags that are shown are specifically filtered down to
  // what the tenant has configured
  let allowedTenantDietaryTemplateTagIds: number[] = [];

  if (tenantPageFilter === 'customizer') {
    allowedTenantDietaryTemplateTagIds = tenantConfig.visibleTagFilters
      .filter((visibleTagFilter) => visibleTagFilter.showInCustomizer)
      .map((visibleTagFilter) => visibleTagFilter.tag.id);
  } else if (tenantPageFilter === 'funnel') {
    allowedTenantDietaryTemplateTagIds = tenantConfig.visibleTagFilters
      .filter((visibleTagFilter) => visibleTagFilter.showInFunnel)
      .map((visibleTagFilter) => visibleTagFilter.tag.id);
  } else {
    allowedTenantDietaryTemplateTagIds = tenantConfig.visibleTagFilters.map(
      (visibleTagFilter) => visibleTagFilter.tag.id,
    );
  }

  const existingDietaryTemplateTagCategory = tagCategories.find(
    (tagCategory) => tagCategory.logicalId === 'DIETARY_TEMPLATE',
  )!; // Assuming this is always found

  // Filter down existing tags to only tenant allow tags, format tags with tenant-supplied
  // display values
  const tenantTags: TagCategory['tags'] =
    existingDietaryTemplateTagCategory.tags
      .filter((tag) => {
        return allowedTenantDietaryTemplateTagIds.includes(tag.id);
      })
      .map((apiTag) => {
        // The position will be used as the sort order priority
        const tenantTagIndex = tenantConfig.visibleTagFilters.findIndex(
          (visibleTagFilter) => visibleTagFilter.tag.id === apiTag.id,
        );
        const tenantTag = tenantConfig.visibleTagFilters[tenantTagIndex];

        return {
          ...apiTag,
          displayName: tenantTag?.displayAs
            ? tenantTag.displayAs
            : apiTag.displayName,
          description: tenantTag?.description
            ? tenantTag?.description
            : apiTag.description,
          /**
           * The Panacea tags table has a `sort_order` column, but we do not use it for dietary template tags. Dietary template
           * tags are always sorted by the position of the tag in the tenant config `visibleTagFilters` array.
           * The Panacea `sort_order` column is used by other types of tags (shelf life), so it does serve a need.
           */
          sortOrder: tenantTagIndex,
        };
      })
      .sort((a, b) => {
        if (a.sortOrder > b.sortOrder) {
          return 1;
        }
        return -1;
      });

  const dietaryTemplateTagCategory = {
    ...existingDietaryTemplateTagCategory,
    tags: tenantTags,
  };
  const filteredTagCategories = [dietaryTemplateTagCategory];

  const topRatedTagCategory = tagCategories.find(
    (tagCategory) => tagCategory.logicalId === 'TOP_RATED_BY_USER',
  );

  const topRatedVisibleTagFilter = tenantConfig.visibleTagFilters.find(
    (visibleTagFilter) => {
      const found = visibleTagFilter.tag.id === 1032;
      if (!found) {
        return false;
      }

      if (tenantPageFilter === 'funnel') {
        return visibleTagFilter.showInFunnel;
      }

      if (tenantPageFilter === 'customizer') {
        return visibleTagFilter.showInCustomizer;
      }

      return true;
    },
  );

  const catalogHasOneOrMoreTopRatedItems = uniqueCatalogTagIds?.includes(1032);
  const shouldDisplayTopRated =
    !uniqueCatalogTagIds || catalogHasOneOrMoreTopRatedItems;
  if (
    topRatedTagCategory &&
    topRatedVisibleTagFilter &&
    shouldDisplayTopRated
  ) {
    filteredTagCategories.push(topRatedTagCategory);
  }

  return filteredTagCategories;
}

/**
 * Ingredients to avoid are just alphabetically sorted. This is purposfully pulling
 * all ingredients to avoid regardless of what's in the catalog.
 */
function getIngredientsToAvoid(input: {
  ingredientsToAvoid: IngredientsToAvoidQuery['ingredientsToAvoid'];
}): IngredientsToAvoidQuery['ingredientsToAvoid'] {
  return [...input.ingredientsToAvoid]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((ingredientToAvoid) => {
      return {
        ...ingredientToAvoid,
        // @TODO: We probably shouldn't have to title case this on the frontend
        displayName: ingredientToAvoid.displayName
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
      };
    });
}

function getMacros(input: {
  catalogs: CatalogsQuery['catalogs'];
}): FilterMacro[] {
  const filterMacros = new Map<string, FilterMacro>();

  input.catalogs.forEach((catalog) => {
    catalog.items.forEach((item) => {
      if (item?.variant.__typename !== 'MealVariant') {
        return;
      }

      const itemMacroNutrients = item.variant.macroNutrients;
      Object.keys(itemMacroNutrients).forEach((macroKey) => {
        // These are keys that exist under this type that we really don't want. The
        // __typename is obvious, but `calories` is an old key that exists that's been
        // deprecated as well.
        if (macroKey === '__typename' || macroKey === 'caloriesInG') {
          return;
        }

        const macroConfig =
          NUTRITION_CATEGORY_CONFIG[
            macroKey as keyof typeof NUTRITION_CATEGORY_CONFIG
          ];

        const existingFilterMacro = filterMacros.get(macroKey);

        const itemMacroValue = itemMacroNutrients[
          macroKey as keyof typeof itemMacroNutrients
        ] as number;

        let unit = '';
        if (macroKey === 'calories') {
          // Calories is an oddity in the Macro for items in that it doesn't contain the `InUnit`
          // suffix like the rest do.
          unit = 'kCal';
        } else {
          // This is just grabbing whatever value is past the string 'In' on the macro
          // value. Ex: `dietaryFiberInG` -> 'g'
          unit = macroKey.includes('In')
            ? macroKey.split('In')[1].toLowerCase()
            : '';
        }

        // If this is the first time encouring this macro, default min to 0
        // and max to provided value (or null if no value existing)
        if (!existingFilterMacro) {
          filterMacros.set(macroKey, {
            key: macroKey,
            displayName: macroConfig?.displayName ?? macroKey,
            min: 0,
            max: itemMacroValue || null,
            unit,
          });

          return;
        }

        // If the macro on this item is larger than the existing macro, replace
        // that as the max value
        if (
          existingFilterMacro.max === null ||
          existingFilterMacro.max < itemMacroValue
        ) {
          filterMacros.set(macroKey, {
            ...existingFilterMacro,
            max: itemMacroValue,
          });

          return;
        }

        // Otherwise, do nothing. The incoming max value is less than the current
      });
    });
  });

  return Array.from(filterMacros.values()).sort((a, b) => {
    const aMacroConfig =
      NUTRITION_CATEGORY_CONFIG[
        a.key as keyof typeof NUTRITION_CATEGORY_CONFIG
      ];
    const bMacroConfig =
      NUTRITION_CATEGORY_CONFIG[
        b.key as keyof typeof NUTRITION_CATEGORY_CONFIG
      ];
    if (aMacroConfig?.sortOrder === bMacroConfig?.sortOrder) {
      return 0;
    }

    return (aMacroConfig?.sortOrder ?? 100) - (bMacroConfig?.sortOrder ?? 100);
  });
}

function getChefs(input: {
  catalogs: CatalogsQuery['catalogs'];
  tenantConfig: ClientSafeTenantConfig;
}): string[] {
  const { catalogs, tenantConfig } = input;

  // If this tenant has disabled showing chefs, this filter should always be empty
  if (tenantConfig.mealDetailProfile.showSupplier === false) {
    return [];
  }

  const uniqueChefNames = new Set<string>();

  catalogs.forEach((catalog) => {
    catalog.items.forEach((item) => {
      if (item?.variant.product.designer) {
        uniqueChefNames.add(item.variant.product.designer.name);
      }
    });
  });

  return Array.from(uniqueChefNames).sort((a, b) => a.localeCompare(b));
}
